/**
 * breakup_mesh — cut a capital hull into its chunks, once per hull model (the physics follow-up's item 7, 2026-10-05).
 *
 * Input: a materialised capital (`obj` — SpaceModule's group; its first child is the wrapped, recentred hull: nose −Z,
 * metres). Every hull triangle is placed in the capital's frame, given to a chunk by lib/space/breakup.ts's recipe, and
 * each chunk becomes its own geometry per source mesh: the triangles' vertices COPIED as they are stored — the mesh's
 * own geometry space, quantisation and all (THE WRAP LAW is about transforming a quantised attribute; nothing here is
 * transformed), so `positionGeometry` is unchanged and the hull's procedural window field (hull_look, metres per unit
 * from `positionGeometry`) reads exactly as it did on the intact hull. The copies are OURS (disposed by the owner); the
 * master's buffers are never shared — disposing a geometry destroys every attribute it holds (three's Geometries).
 *
 * Each chunk's placement in its principal (body) frame is baked into one matrix per mesh: M = Aᵀ · T(−com) · rel, where
 * rel = the mesh relative to the capital, com the chunk's centre of mass and A its principal axes — so the chunk's root
 * carries exactly the rigid body: position = the centre of mass, quaternion = the body frame's orientation.
 */
import * as THREE from 'three/webgpu';
import {
  breakupRecipe, splitTriangles, shellInertia, principal, quatFromAxes, rawChunkOf, onCut, BREAKUP,
  type V3, type Q4, type BreakupRecipe,
} from './breakup.js';

export interface ChunkMesh {
  geo: THREE.BufferGeometry;
  material: THREE.Material;
  /** The mesh's matrix under the chunk root (body frame): Aᵀ · T(−com) · rel. */
  matrix: THREE.Matrix4;
  /** Metres per unit of the mesh's own geometry space (a quantised hull's units are ~2.4 km; the procedural one's 1 m). */
  unitM: number;
}
export interface ChunkModel {
  meshes: ChunkMesh[];
  /** The centre of mass in the capital's frame (m). */
  com: V3;
  /** The principal axes (body → capital frame) as a quaternion [w, x, y, z]. */
  axes: Q4;
  /** Principal moments (area · m², ascending). */
  I: V3;
  /** The chunk's longest extent (m) — its spin rate scales as 1 / length. */
  lengthM: number;
  /** Seats on the cut faces (body frame) with their outward normals — where it vents. */
  seats: Array<{ b: V3; n: V3 }>;
  tris: number;
  area: number;
}
export interface BreakupModel {
  key: string;
  recipe: BreakupRecipe;
  chunks: ChunkModel[];
  tris: number;
  buildMs: number;
}

interface Src { mesh: THREE.Mesh; rel: THREE.Matrix4; first: number; count: number }

/** Copy the listed vertices of `geo` (every attribute, stored values verbatim) into a new compact geometry. */
function compact(geo: THREE.BufferGeometry, triVerts: number[]): THREE.BufferGeometry {
  const remap = new Map<number, number>();
  const order: number[] = [];
  const idx = new Array<number>(triVerts.length);
  for (let i = 0; i < triVerts.length; i++) {
    const v = triVerts[i];
    let r = remap.get(v);
    if (r === undefined) { r = order.length; remap.set(v, r); order.push(v); }
    idx[i] = r;
  }
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(geo.attributes)) {
    const a = geo.getAttribute(name) as THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
    const k = a.itemSize;
    const arr = (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute ? (a as THREE.InterleavedBufferAttribute).data.array : (a as THREE.BufferAttribute).array;
    const Ctor = (arr as unknown as { constructor: new (n: number) => THREE.TypedArray }).constructor;
    const dst = new Ctor(order.length * k);
    const na = new THREE.BufferAttribute(dst, k, a.normalized);
    for (let i = 0; i < order.length; i++) for (let c = 0; c < k; c++) {
      // getComponent/setComponent move the STORED value (no (de)normalisation) — the quantised positions copy exactly
      na.setComponent(i, c, (a as THREE.BufferAttribute).getComponent(order[i], c));
    }
    out.setAttribute(name, na);
  }
  out.setIndex(order.length > 65535 ? new THREE.Uint32BufferAttribute(idx, 1) : new THREE.Uint16BufferAttribute(idx, 1));
  out.computeBoundingSphere();
  return out;
}

/**
 * Cut `obj`'s hull (its first child) into chunks with the recipe named by `key`. Returns null when there is no hull mesh.
 * A few milliseconds for an 18k-triangle capital; the caller caches the model per hull model and variant.
 */
export function buildBreakupModel(obj: THREE.Object3D, key: string): BreakupModel | null {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  const hull = obj.children[0];
  if (!hull) return null;
  obj.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(obj.matrixWorld).invert();
  const srcs: Src[] = [];
  let tris = 0;
  hull.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m as unknown as THREE.InstancedMesh).isInstancedMesh || Array.isArray(m.material)) return;
    const g = m.geometry as THREE.BufferGeometry | undefined;
    const pos = g?.getAttribute('position');
    if (!g || !pos) return;
    const n = (g.index ? g.index.count : pos.count) / 3 | 0;
    if (n <= 0) return;
    srcs.push({ mesh: m, rel: new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld), first: tris, count: n });
    tris += n;
  });
  if (!tris) return null;
  // every triangle's centroid and area in the capital's frame
  const cen = new Float64Array(tris * 3), area = new Float64Array(tris);
  const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3(), e1 = new THREE.Vector3(), e2 = new THREE.Vector3();
  const box = new THREE.Box3();
  const triV = (s: Src, t: number, j: number): number => { const g = s.mesh.geometry; return g.index ? g.index.getX(t * 3 + j) : t * 3 + j; };
  for (const s of srcs) {
    const pos = s.mesh.geometry.getAttribute('position');
    for (let t = 0; t < s.count; t++) {
      va.fromBufferAttribute(pos, triV(s, t, 0)).applyMatrix4(s.rel);
      vb.fromBufferAttribute(pos, triV(s, t, 1)).applyMatrix4(s.rel);
      vc.fromBufferAttribute(pos, triV(s, t, 2)).applyMatrix4(s.rel);
      const i = s.first + t;
      cen[i * 3] = (va.x + vb.x + vc.x) / 3; cen[i * 3 + 1] = (va.y + vb.y + vc.y) / 3; cen[i * 3 + 2] = (va.z + vb.z + vc.z) / 3;
      area[i] = e1.subVectors(vb, va).cross(e2.subVectors(vc, va)).length() * 0.5;
      box.expandByPoint(va); box.expandByPoint(vb); box.expandByPoint(vc);
    }
  }
  const size = box.getSize(new THREE.Vector3());
  const recipe = breakupRecipe(key, size.z, size.x / 2, size.y / 2);
  const split = splitTriangles(cen, area, recipe);
  const band = BREAKUP.cutBand * size.z;
  // one pass: every chunk's triangles, per source mesh (the per-chunk loops below never walk the whole hull again)
  const lists: number[][][] = Array.from({ length: split.count }, () => srcs.map(() => [] as number[]));
  for (let si = 0; si < srcs.length; si++) {
    const s = srcs[si];
    for (let t = 0; t < s.count; t++) lists[split.chunkOf[s.first + t]][si].push(t);
  }
  const chunks: ChunkModel[] = [];
  for (let k = 0; k < split.count; k++) {
    const com = split.centroid[k];
    const mine = new Set<number>();
    for (let si = 0; si < srcs.length; si++) for (const t of lists[k][si]) mine.add(srcs[si].first + t);
    const sub = [...mine];
    const subC = new Float64Array(sub.length * 3), subA = new Float64Array(sub.length);
    for (let j = 0; j < sub.length; j++) { const i = sub[j]; subC[j * 3] = cen[i * 3]; subC[j * 3 + 1] = cen[i * 3 + 1]; subC[j * 3 + 2] = cen[i * 3 + 2]; subA[j] = area[i]; }
    const pr = principal(shellInertia(subC, subA, () => true, com));
    const A = pr.A;
    // body → capital rotation as a matrix (columns = the principal axes); its transpose takes capital → body
    const Am = new THREE.Matrix4().set(
      A[0][0], A[1][0], A[2][0], 0,
      A[0][1], A[1][1], A[2][1], 0,
      A[0][2], A[1][2], A[2][2], 0,
      0, 0, 0, 1,
    );
    const AT = Am.clone().transpose();
    const toBody = new THREE.Matrix4().multiplyMatrices(AT, new THREE.Matrix4().makeTranslation(-com[0], -com[1], -com[2]));
    const meshes: ChunkMesh[] = [];
    let lo = Infinity, hi = -Infinity;
    const seats: Array<{ b: V3; n: V3; i: number }> = [];
    for (let si = 0; si < srcs.length; si++) {
      const s = srcs[si];
      const verts: number[] = [];
      for (const t of lists[k][si]) {
        const i = s.first + t;
        verts.push(triV(s, t, 0), triV(s, t, 1), triV(s, t, 2));
        // the extent along the chunk's long (first) principal axis
        const dx = cen[i * 3] - com[0], dy = cen[i * 3 + 1] - com[1], dz = cen[i * 3 + 2] - com[2];
        const u = dx * A[0][0] + dy * A[0][1] + dz * A[0][2];
        if (u < lo) lo = u; if (u > hi) hi = u;
        const raw = rawChunkOf(recipe, cen[i * 3], cen[i * 3 + 1], cen[i * 3 + 2]);
        const pl = onCut(recipe, raw, cen[i * 3], cen[i * 3 + 1], cen[i * 3 + 2], band);
        if (pl) {
          const sg = pl.n[0] * dx + pl.n[1] * dy + pl.n[2] * dz > 0 ? 1 : -1;
          const nx = pl.n[0] * sg, ny = pl.n[1] * sg, nz = pl.n[2] * sg;
          seats.push({
            b: [dx * A[0][0] + dy * A[0][1] + dz * A[0][2], dx * A[1][0] + dy * A[1][1] + dz * A[1][2], dx * A[2][0] + dy * A[2][1] + dz * A[2][2]],
            n: [nx * A[0][0] + ny * A[0][1] + nz * A[0][2], nx * A[1][0] + ny * A[1][1] + nz * A[1][2], nx * A[2][0] + ny * A[2][1] + nz * A[2][2]],
            i,
          });
        }
      }
      if (!verts.length) continue;
      meshes.push({ geo: compact(s.mesh.geometry, verts), material: s.mesh.material as THREE.Material, matrix: new THREE.Matrix4().multiplyMatrices(toBody, s.rel), unitM: s.rel.getMaxScaleOnAxis() });
    }
    // up to `ventSeats` seats spread over the cut face (a stride through it, not the first few in a corner)
    const pick: Array<{ b: V3; n: V3 }> = [];
    if (seats.length) {
      const step = Math.max(1, Math.floor(seats.length / BREAKUP.ventSeats));
      for (let j = 0; j < seats.length && pick.length < BREAKUP.ventSeats; j += step) pick.push({ b: seats[j].b, n: seats[j].n });
    }
    chunks.push({ meshes, com, axes: quatFromAxes(A), I: pr.I, lengthM: Math.max(1, hi - lo), seats: pick, tris: split.tris[k], area: split.area[k] });
  }
  const buildMs = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
  return { key, recipe, chunks, tris, buildMs };
}

/** Free a model's geometries (ours; the materials are the hull's — never freed here). */
export function disposeBreakupModel(m: BreakupModel, free: (d: { dispose(): void }) => void): void {
  for (const c of m.chunks) for (const cm of c.meshes) free(cm.geo);
}
